import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../lib/api/utils';
import { motion, AnimatePresence } from 'framer-motion';

// Helper component for the loading placeholder
const LoadingPlaceholder: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`bg-[#202020] rounded-md animate-pulse ${className}`}></div>
);

const StatsBar = () => {
  const [highlightCount, setHighlightCount] = useState<number | null>(null);
  const [streamCount, setStreamCount] = useState<number | null>(null);

  useEffect(() => {
    // WebSocket for highlight count
    const highlightWs = new WebSocket(`${BASE_URL}/ws/highlight-count`);
    highlightWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.highlight_count !== undefined) {
          setHighlightCount(data.highlight_count);
        }
      } catch (error) {
        console.error('Error parsing highlight count:', error);
      }
    };
    highlightWs.onerror = (error) => {
      console.error('Highlight WebSocket error:', error);
    };
    highlightWs.onclose = () => {
      console.log('Highlight WebSocket closed');
    };

    // WebSocket for stream count
    const streamWs = new WebSocket(`${BASE_URL}/ws/live-stream-count`);
    streamWs.onmessage = (event) => {
       try {
        const data = JSON.parse(event.data);
         if (data.live_stream_count !== undefined) {
          setStreamCount(data.live_stream_count);
        }
      } catch (error) {
        console.error('Error parsing stream count:', error);
      }
    };
    streamWs.onerror = (error) => {
      console.error('Stream WebSocket error:', error);
    };
    streamWs.onclose = () => {
      console.log('Stream WebSocket closed');
    };

    // Cleanup function
    return () => {
      highlightWs.close();
      streamWs.close();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  const formatNumber = (num: number | null): string => {
    if (num === null) return ''; // Return empty string, loading handled by placeholder
    return num.toLocaleString('en-US');
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-[#0b0b0c] backdrop-blur-sm text-black dark:text-white p-2 sm:p-2 md:p-3 rounded-t-xl max-w-md md:max-w-xl lg:max-w-2xl mx-auto">
      <div className="flex items-center justify-center space-x-4 sm:space-x-6 md:space-x-8 text-xs sm:text-sm md:text-base">
        <div className="flex items-center h-10">
          <AnimatePresence mode="wait">
            {highlightCount === null ? (
              <motion.div
                key="highlight-loader"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <LoadingPlaceholder className="h-6 w-12 sm:h-7 sm:w-14 md:h-8 md:w-16 lg:h-10 lg:w-20 mr-1 sm:mr-2" />
              </motion.div>
            ) : (
              <motion.span
                key="highlight-count"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="font-bold text-xs sm:text-sm md:text-lg lg:text-xl mr-1 sm:mr-2"
              >
                {formatNumber(highlightCount)}
              </motion.span>
            )}
          </AnimatePresence>
          <span className="text-muted-foreground text-xs sm:text-sm md:text-lg lg:text-xl">highlights created</span>
        </div>
        <div className="border-l border-white/20 h-5 md:h-7 lg:h-8"></div>
        <div className="flex items-center h-10">
          <AnimatePresence mode="wait">
             {streamCount === null ? (
               <motion.div
                 key="stream-loader"
                 initial={{ opacity: 1 }}
                 exit={{ opacity: 0 }}
                 transition={{ duration: 0.2 }}
               >
                 <LoadingPlaceholder className="h-6 w-8 sm:h-7 sm:w-10 md:h-8 md:w-12 lg:h-10 lg:w-14 mr-1 sm:mr-2" />
               </motion.div>
             ) : (
              <motion.span
                key="stream-count"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="font-bold text-xs sm:text-sm md:text-lg lg:text-xl mr-1 sm:mr-2"
              >
                {formatNumber(streamCount)}
              </motion.span>
             )}
           </AnimatePresence>
           <span className="text-muted-foreground text-xs sm:text-sm md:text-lg lg:text-xl">streams</span>
        </div>
        <div className="flex items-center bg-accent rounded-lg px-2 sm:px-3 py-0.5 sm:py-1">
          <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5 mr-1.5 sm:mr-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-red-500"></span>
          </span>
          <span className="text-muted-foreground font-medium text-xs sm:text-sm lg:text-lg">Live</span>
        </div>
      </div>
    </div>
  );
};

export default StatsBar; 